import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref as dbRef, onValue } from 'firebase/database';
import { 
  FaSearch,
  FaCopy,
  FaFileExcel,
  FaPrint,
  FaArrowLeft,
  FaCalendarAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const EngineerOtherTransferWorks = () => {
    const navigate = useNavigate();
    const [works, setWorks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const entriesPerPage = 10;

    // Fetch works
    useEffect(() => {
        const worksRef = dbRef(db, 'ekamwatapzp/WorkNames');
        const unsubscribe = onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let flattenedWorks = [];
                
                Object.entries(data).forEach(([key, val]) => {
                    if (val && typeof val === 'object') {
                        // Check if it's a direct work object (has schemeName)
                        if (val.schemeName) {
                            if (val.sharedwork === 'खुला') {
                                flattenedWorks.push({
                                    id: key,
                                    meetingNumber: val.meetingNumber || val.category || 'General',
                                    ...val
                                });
                            }
                        } else {
                            // It's a category/meeting folder
                            const meetingNum = key;
                            Object.entries(val).forEach(([id, value]) => {
                                if (value && typeof value === 'object' && value.sharedwork === 'खुला') {
                                    flattenedWorks.push({
                                        id,
                                        meetingNumber: meetingNum,
                                        ...value
                                    });
                                }
                            });
                        }
                    }
                });
                
                setWorks(flattenedWorks.reverse()); 
            } else {
                setWorks([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const filteredWorks = works.filter(work => 
        (work.workName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (work.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (work.taluka || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (work.schemeName || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const indexOfLastEntry = currentPage * entriesPerPage;
    const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
    const currentEntries = filteredWorks.slice(indexOfFirstEntry, indexOfLastEntry);
    const totalPages = Math.ceil(filteredWorks.length / entriesPerPage);

    const downloadExcel = () => {
        const exportData = filteredWorks.map((item, index) => ({
            'अ.क्र.': index + 1,
            'तालुका': item.taluka || '-',
            'योजनेचे नाव': item.schemeName || '-',
            'निविदा रक्कम(Lakh)': item.tenderAmount || '0',
            'कामाचे नाव': item.workName || '-'
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "खुला वर्ग हस्तांतरण कामे");
        XLSX.writeFile(wb, `खुला_वर्ग_हस्तांतरण_कामे_${new Date().toLocaleDateString()}.xlsx`);
        toast.info("एक्सेल फाईल डाउनलोड होत आहे...");
    };

    const copyToClipboard = () => {
        const headers = ["अ.क्र.", "तालुका", "योजनेचे नाव", "निविदा रक्कम (Lakh)", "कामाचे नाव"];
        const rows = filteredWorks.map((item, index) => [
            index + 1,
            item.taluka || '-',
            item.schemeName || '-',
            item.tenderAmount || '0',
            item.workName || '-'
        ]);

        const text = [headers, ...rows].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(text).then(() => {
            toast.success("माहिती कॉपी झाली! (Copied to Clipboard)");
        }).catch(err => {
            toast.error("कॉपी करताना त्रुटी आली.");
        });
    };

    const handlePrint = () => {
        const printContent = `
            <html>
                <head>
                    <title>खुला वर्ग हस्तांतरण कामाची यादी - सांगली जिल्हा परिषद</title>
                    <style>
                        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Marathi:wght@400;700&display=swap');
                        body { font-family: 'Noto Sans Marathi', sans-serif; padding: 40px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 30px; }
                        th, td { border: 1px solid #e2e8f0; padding: 12px 15px; text-align: left; font-size: 13px; }
                        th { background-color: #f8fafc; color: #475569; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
                        td { color: #1e293b; }
                        h2 { text-align: center; color: #0f172a; margin-bottom: 10px; font-size: 24px; }
                        p { text-align: center; color: #64748b; font-size: 14px; margin-top: 0; }
                        .header { border-bottom: 2px solid #085e53; padding-bottom: 20px; margin-bottom: 30px; }
                        .print-time { text-align: right; font-size: 11px; color: #94a3b8; margin-top: 30px; border-top: 1px solid #f1f5f9; padding-top: 10px; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h2>खुला वर्ग हस्तांतरण कामाची यादी</h2>
                        <p>सांगली जिल्हा परिषद (Zilla Parishad Sangli)</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th style="text-align: center; width: 50px;">अ.क्र.</th>
                                <th>तालुका</th>
                                <th>योजनेचे नाव</th>
                                <th>निविदा रक्कम(Lakh)</th>
                                <th>कामाचे नाव</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${filteredWorks.map((item, index) => `
                                <tr>
                                    <td style="text-align: center;">${index + 1}</td>
                                    <td>${item.taluka || '-'}</td>
                                    <td>${item.schemeName || '-'}</td>
                                    <td>${item.tenderAmount || '0'}</td>
                                    <td>${item.workName || '-'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div class="print-time">अहवाल दिनांक: ${new Date().toLocaleString()}</div>
                </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
            printWindow.print();
            printWindow.close();
        };
    };

    if (loading) {
        return (
            <EngineerLayout>
                <div className="min-h-screen flex items-center justify-center bg-gray-50/50">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#10b981]"></div>
                </div>
            </EngineerLayout>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-6 md:p-8 bg-gray-50/30 min-h-screen">
                {/* Dark Header Card */}
                <div className="bg-[#1a1c23] rounded-3xl p-10 mb-8 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl transition-transform duration-1000 group-hover:scale-110"></div>
                    
                    <div className="relative flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-6">
                            <button 
                                onClick={() => navigate('/engineer/work-reports-menu')}
                                className="bg-white/10 hover:bg-white/20 text-white p-4 rounded-2xl transition-all hover:scale-110 active:scale-90 backdrop-blur-sm border border-white/10"
                            >
                                <FaArrowLeft size={20} />
                            </button>
                            <div>
                                <h1 className="text-4xl font-bold text-white tracking-tight mb-3">Other Transfer Works</h1>
                                <p className="text-gray-400 font-bold text-sm tracking-widest uppercase">
                                    Zilla Parishad / <span className="text-[#10b981]">Other Transfer Works</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-[2rem] shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
                    {/* Toolbar */}
                    <div className="p-8 flex flex-col lg:flex-row justify-between items-center gap-8 bg-gray-50/30 border-b border-gray-100">
                        <div className="flex items-center gap-3 w-full lg:w-auto overflow-x-auto pb-4 lg:pb-0 scrollbar-hide">
                            <button onClick={copyToClipboard} className="flex items-center gap-2 px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-xl text-[13px] font-bold shadow-sm transition-all hover:scale-105 active:scale-95 whitespace-nowrap">
                                <FaCopy className="text-blue-500" /> Copy
                            </button>
                            <button onClick={downloadExcel} className="flex items-center gap-2 px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-xl text-[13px] font-bold shadow-sm transition-all hover:scale-105 active:scale-95 whitespace-nowrap">
                                <FaFileExcel className="text-green-500" /> Excel
                            </button>
                            <button onClick={handlePrint} className="flex items-center gap-2 px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-xl text-[13px] font-bold shadow-sm transition-all hover:scale-105 active:scale-95 whitespace-nowrap">
                                <FaPrint className="text-gray-500" /> Print
                            </button>
                        </div>

                        <div className="relative w-full lg:w-96 group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaSearch className="text-gray-400 group-focus-within:text-[#10b981] transition-colors" />
                            </div>
                            <input 
                                type="text" 
                                className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-[#10b981]/5 focus:border-[#10b981] outline-none transition-all shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search Name Of Work, Taluka, Scheme..."
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-gray-50/50">
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 text-center w-16">#</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 text-left">Taluka</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 text-left">Scheme Name</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 text-right">Niveeda Rakkam (Lakh)</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 text-left min-w-[300px]">Name Of Work</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50/80">
                                {currentEntries.map((work, index) => (
                                    <tr key={work.id} className="hover:bg-blue-50/30 transition-all duration-300">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-500 text-center">{indexOfFirstEntry + index + 1}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-700">{work.taluka || '-'}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#10b981]">{work.schemeName || '-'}</td>
                                        <td className="px-6 py-4 text-sm font-bold text-gray-800 text-right whitespace-nowrap">{work.tenderAmount || "0"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-800 leading-relaxed min-w-[300px]">{work.workName || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {currentEntries.length === 0 && (
                            <div className="py-24 text-center flex flex-col items-center gap-6">
                                <div className="p-8 bg-gray-50 rounded-full text-gray-200">
                                    <FaSearch size={64} />
                                </div>
                                <div className="space-y-2">
                                    <p className="text-gray-500 font-bold text-xl tracking-tight">No data available in table</p>
                                    <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Please try again with a different search term</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Pagination & Footer */}
                    <div className="p-8 border-t border-gray-50 bg-gray-50/30 flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="text-[11px] font-bold text-gray-400 tracking-widest uppercase">
                            <span className="bg-[#10b981]/10 text-[#10b981] px-3 py-1 rounded-lg">
                                Showing {indexOfFirstEntry + 1} to {Math.min(indexOfLastEntry, filteredWorks.length)} of {filteredWorks.length} entries
                            </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <button 
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => prev - 1)}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-sm border ${currentPage === 1 ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed' : 'bg-white text-gray-600 border-gray-200 hover:border-[#10b981] hover:text-[#10b981] active:scale-95'}`}
                            >
                                Previous
                            </button>
                            <div className="w-10 h-10 flex items-center justify-center bg-[#10b981] text-white rounded-xl text-sm font-bold shadow-md">
                                {currentPage}
                            </div>
                            <button 
                                disabled={currentPage === totalPages || totalPages === 0}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-sm border ${currentPage === totalPages || totalPages === 0 ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed' : 'bg-white text-gray-600 border-gray-200 hover:border-[#10b981] hover:text-[#10b981] active:scale-95'}`}
                            >
                                Next
                            </button>
                        </div>
                        
                        <div className="hidden lg:flex items-center gap-2 opacity-60 text-[11px] font-bold text-gray-400 tracking-widest uppercase">
                            <FaCalendarAlt /> Zilla Parishad Sangli • {new Date().getFullYear()}
                        </div>
                    </div>
                </div>
            </div>
        </EngineerLayout>
    );
};

export default EngineerOtherTransferWorks;
